import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import FundingReportChartCard, { FundingReportBarChart } from './FundingReportChartCard';
import { useFundingRoutes } from '../../hooks/funding/useFundingRoutes';

const formatCurrency = (value) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value || 0);

const FundingLenderReport = ({ data, loading }) => {
  const { lenders: lendersPath } = useFundingRoutes();
  const tierItems = useMemo(() => Object.entries(data?.matchTierDistribution || {}).map(([label, value]) => ({
    label,
    value,
  })), [data]);

  if (loading) return <p className="text-sm text-[var(--fg-muted)]">Loading lender report...</p>;
  if (!data) return <p className="text-sm text-[var(--fg-muted)]">No lender data available.</p>;

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-xl border border-[var(--fg-line)] bg-[var(--fg-panel-soft)] px-4 py-3">
          <div className="text-xs text-[var(--fg-muted)]">Average match score</div>
          <div className="text-lg font-bold text-[var(--fg-accent)]">{data.averageMatchScore ?? 0}%</div>
        </div>
        <div className="rounded-xl border border-[var(--fg-line)] bg-[var(--fg-panel-soft)] px-4 py-3">
          <div className="text-xs text-[var(--fg-muted)]">Estimated matched funding</div>
          <div className="text-lg font-bold text-[var(--fg-text)]">{formatCurrency(data.estimatedMatchedFunding)}</div>
        </div>
        <div className="rounded-xl border border-[var(--fg-line)] bg-[var(--fg-panel-soft)] px-4 py-3">
          <div className="text-xs text-[var(--fg-muted)]">Match to submission</div>
          <div className="text-lg font-bold text-[var(--fg-text)]">{data.submittedVsMatchedConversion ?? 0}%</div>
        </div>
        <div className="rounded-xl border border-[var(--fg-line)] bg-[var(--fg-panel-soft)] px-4 py-3">
          <div className="text-xs text-[var(--fg-muted)]">Matches / Submissions</div>
          <div className="text-lg font-bold text-[var(--fg-text)]">{data.totalMatches ?? 0} / {data.totalSubmissions ?? 0}</div>
        </div>
      </div>
      <FundingReportChartCard title="Match tier distribution" subtitle="Fit tier breakdown">
        <FundingReportBarChart items={tierItems} />
      </FundingReportChartCard>
      <div className="overflow-x-auto">
        <table className="min-w-full text-left text-sm">
          <thead>
            <tr className="border-b border-[var(--fg-line)] text-[var(--fg-muted)]">
              <th className="px-3 py-2">Lender</th>
              <th className="px-3 py-2">Matches</th>
              <th className="px-3 py-2">Avg Score</th>
              <th className="px-3 py-2">Estimated Funding</th>
            </tr>
          </thead>
          <tbody>
            {(data.topMatchedLenders || []).map((row) => (
              <tr key={row.lenderId || row.lenderName} className="border-b border-[var(--fg-line)]/70">
                <td className="px-3 py-2">
                  {row.lenderId ? (
                    <Link to={`${lendersPath}/${row.lenderId}`} className="text-[var(--fg-accent)] hover:underline">
                      {row.lenderName}
                    </Link>
                  ) : row.lenderName}
                </td>
                <td className="px-3 py-2">{row.matchCount}</td>
                <td className="px-3 py-2">{row.averageMatchScore}%</td>
                <td className="px-3 py-2">{formatCurrency(row.estimatedFunding)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-left text-sm">
          <thead>
            <tr className="border-b border-[var(--fg-line)] text-[var(--fg-muted)]">
              <th className="px-3 py-2">Lender</th>
              <th className="px-3 py-2">Product</th>
              <th className="px-3 py-2">Submissions</th>
              <th className="px-3 py-2">Approved</th>
              <th className="px-3 py-2">Funded</th>
            </tr>
          </thead>
          <tbody>
            {(data.lenderProductPerformance || []).map((row) => (
              <tr key={`${row.lenderName}-${row.productType}`} className="border-b border-[var(--fg-line)]/70">
                <td className="px-3 py-2">{row.lenderName}</td>
                <td className="px-3 py-2">{row.productType}</td>
                <td className="px-3 py-2">{row.submissions}</td>
                <td className="px-3 py-2">{row.approved}</td>
                <td className="px-3 py-2">{row.funded}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default FundingLenderReport;
